import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";

const LINKS = [
  { label: "About", to: "/about" },
  { label: "Welcome", to: "/welcome" },
  { label: "Table", to: "/table" },
  { label: "Restore", to: "/restore" },
  { label: "Partners", to: "/partners" },
  { label: "Membership", to: "/membership" },
];

export default function Nav() {
  const location = useLocation();
  const isLanding = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // On subpages the nav is always visible; on landing it hides until scroll.
  const visible = !isLanding || scrolled;

  return (
    <motion.header
      initial={false}
      animate={{ y: visible ? 0 : -100, opacity: visible ? 1 : 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 ${visible ? "" : "pointer-events-none"}`}
      data-testid="site-nav"
    >
      <div className="transition-colors duration-500 bg-black/70 backdrop-blur-2xl border-b border-white/10">
        <nav className="mx-auto max-w-[1600px] px-6 md:px-12 h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex flex-col items-start leading-none"
          >
            <span className="font-serif text-lg md:text-xl tracking-tight text-ink">
              Lodge &amp; Retreat
            </span>
            <span className="font-sans text-[0.55rem] tracking-[0.4em] uppercase text-gold -mt-0.5">
              Partners
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-10">
            {LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.slice(1)}`}
                className={`font-sans text-sm tracking-wide transition-colors duration-300 ${
                  location.pathname === l.to
                    ? "text-gold"
                    : "text-ash hover:text-ink"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <Link
              to="/book"
              data-testid="nav-book-btn"
              className="hidden sm:inline-flex items-center rounded-full bg-black border border-gold/70 px-6 py-2.5 font-sans text-xs tracking-[0.15em] uppercase text-metallic-gold hover:border-gold hover:bg-black/80 transition-all duration-300"
            >
              Get in Touch for a Chat
            </Link>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="nav-mobile-toggle"
              className="lg:hidden flex flex-col gap-1.5 p-2"
              aria-label="Menu"
            >
              <span className="w-6 h-px bg-ink" />
              <span className="w-6 h-px bg-ink" />
            </button>
          </div>
        </nav>
      </div>

      {open && (
        <div
          className="lg:hidden bg-black/95 backdrop-blur-2xl border-b border-white/10 px-6 py-6 flex flex-col gap-4"
          data-testid="nav-mobile-menu"
        >
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-mobile-link-${l.to.slice(1)}`}
              onClick={() => setOpen(false)}
              className="font-serif text-2xl text-ink text-left"
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/book"
            data-testid="nav-mobile-book-btn"
            onClick={() => setOpen(false)}
            className="mt-2 rounded-full bg-black border border-gold/70 px-6 py-3 font-sans text-xs tracking-[0.15em] uppercase text-metallic-gold text-left w-max"
          >
            Get in Touch for a Chat
          </Link>
        </div>
      )}
    </motion.header>
  );
}
